import { format as formatDate, isValid as isValidDate, parse as parseDate, type Locale } from 'date-fns';
import IMask from 'imask';
import { useCallback, useEffect, useMemo } from 'react';
import { useIMask } from 'react-imask';

// IMask's day/month `MaskedRange` blocks (1-31, 1-12) mark themselves "complete"
// the moment a single leftover digit can no longer extend to a valid value in
// range (e.g. day "9" - 90-99 is never <= 31). That flips the whole pattern's
// completeness, which triggers a validation pass that can reset the entire field
// - reproduced directly against IMask: deleting the leading digit of "09/01/2026"
// blanked the input outright, with no code of ours in the call path. Widening
// both blocks to 0-99 removes the early-completion trigger entirely; genuinely
// out-of-range values (month 13, day 32) are still caught by our own parse +
// isValid check below, same as any other invalid/incomplete date.
const DATE_BLOCKS = {
  d: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2 },
  m: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2 },
};

interface DateTextInputProps {
  /** Committed value to display. `null` shows the field empty (no error - just unset). */
  value: Date | null;
  /** IMask pattern dialect, e.g. `DD.MM.YYYY` - controls block widths and separators. */
  pattern: string;
  /** date-fns format dialect, e.g. `dd.MM.yyyy` - used to parse/format the typed text. */
  displayFormat: string;
  locale: Locale;
  placeholder?: string;
  ariaLabel?: string;
  invalid?: boolean;
  /**
   * Fires on every keystroke with whether the field currently holds a complete,
   * parseable date - drives the Apply-disabled and red-outline state live, per AC.
   */
  onLiveChange?: (isComplete: boolean) => void;
  /**
   * Fires on blur/Enter only, with the parsed date (or `null` if incomplete/invalid).
   * Kept separate from `onLiveChange` so the calendar doesn't jump mid-keystroke.
   */
  onCommit: (date: Date | null) => void;
}

export const DateTextInput: React.FC<DateTextInputProps> = (props) => {
  const { value, pattern, displayFormat, locale, placeholder, ariaLabel, invalid, onLiveChange, onCommit } = props;

  // `useIMask` re-runs its own internal effect (and calls `mask.updateOptions`)
  // whenever `opts` or `onAccept` change reference - unmemoized, that fires on
  // every keystroke (onAccept -> onLiveChange -> parent re-render -> new object
  // literal here), which resets the mask mid-type. Stable references make the
  // library only reconfigure when pattern/format/locale actually change.
  // `date instanceof Date` is true even for an Invalid Date (e.g. new Date(NaN)),
  // so an upstream value that's momentarily unset can still reach here as a real
  // Date object - date-fns' `format` throws RangeError on those. Guarded below.
  //
  // On an incomplete edit (delete a digit mid-date), IMask itself re-derives the
  // display by calling `format(typedValue, masked)` with `typedValue: null` - so
  // returning '' here would wipe out whatever the user has typed so far, not just
  // guard against the crash. Falling back to `masked.value` (what's already on
  // screen) instead leaves an in-progress edit alone.
  const format = useCallback(
    (date: Date, masked?: { value?: string }) =>
      isValidDate(date) ? formatDate(date, displayFormat, { locale }) : masked?.value ?? '',
    [displayFormat, locale],
  );
  // Returns `null` rather than an Invalid Date for unparseable input - `null` is
  // the type's actual "absent" value (`DateValue = Date | null`); an Invalid Date
  // object still passes truthy/`!= null` checks everywhere else in this file.
  const parse = useCallback(
    (str: string) => {
      const parsed = parseDate(str, displayFormat, new Date(), { locale });
      return isValidDate(parsed) ? parsed : null;
    },
    [displayFormat, locale],
  );
  const opts = useMemo(() => ({ mask: Date, pattern, format, parse, blocks: DATE_BLOCKS }), [pattern, format, parse]);
  const onAccept = useCallback((_value: string, mask: any) => onLiveChange?.(mask?.typedValue != null), [onLiveChange]);

  const { ref, maskRef, value: displayValue, setTypedValue } = useIMask(opts, { onAccept });

  // External sync (calendar click, preset, close-without-apply) - mirrors MaskedInput's
  // own resync effect. Compared by `.valueOf()` since `value` is a fresh object per render.
  // An Invalid Date is treated as unset rather than forwarded to the mask. Skipped while
  // this field has focus - it exists for changes from *outside* this input, and should
  // never overwrite an edit the user is in the middle of typing.
  const validValue = isValidDate(value) ? value : null;
  useEffect(() => {
    if (document.activeElement === ref.current) return;
    const current = maskRef.current?.masked?.typedValue ?? null;
    const currentTs = current instanceof Date ? current.valueOf() : null;
    const nextTs = validValue instanceof Date ? validValue.valueOf() : null;
    if (currentTs !== nextTs) {
      setTypedValue(validValue);
    }
  }, [validValue]);

  const commit = () => onCommit(maskRef.current?.masked?.typedValue ?? null);

  return (
    <input
      ref={ref as any}
      className="date-text-input"
      value={displayValue}
      onChange={() => {}}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && commit()}
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
    />
  );
};
